import React, {useRef, useState, useEffect, useMemo} from 'react';
import {BaseInputProps} from '../..';
import VideoPlayer from 'shared/components/videojs';
import {useController, useFormContext} from 'react-hook-form';
import {NestedKeys} from 'shared/types/key-manipulation';
import {
  Container,
  Edit,
  FileAddContainer,
  Input,
  Label,
  Thumbnail,
} from './styles';
import usei18next from 'shared/hooks/use-i18next';
import {AiTwotoneEdit} from 'react-icons/ai';
import Dropzone from 'shared/migration-components/dropzone';
import Modal from 'shared/migration-components/modal';
import ShadowContainer from 'shared/migration-components/shadow-container';
import {MIME_TYPES} from '@mantine/dropzone';

export type VideoProps<T = any> = BaseInputProps & {
  name: NestedKeys<T>;
  gridColumn?: number;
};

const VideInput: React.FC<VideoProps> = ({
  name,
  label,
  disabled,
  gridColumn,
  border,
}) => {
  const {control} = useFormContext();
  const {field} = useController({control, name: name!});

  const [file, setFile] = useState<File>(field.value);
  const [openedModal, setOpenedModal] = useState<boolean>(false);
  const [showVideoPreview, setShowVideoPreview] = useState<boolean>(false);

  const videoRef = useRef<HTMLVideoElement | null>(null);

  const {translate} = usei18next();

  const playerOptions = useMemo(
    () => ({
      source:
        (typeof file === 'string' ? file : file && URL.createObjectURL(file)) ||
        '',
      type: MIME_TYPES.mp4,
    }),
    [file],
  );

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
    }
  }, [playerOptions]);

  const onChange = (file: File) => {
    setOpenedModal(false);
    setFile(file);
    field.onChange(file);
  };

  const handleFileClick = () => !disabled && setOpenedModal(true);

  return (
    <Container {...{gridColumn, border}}>
      {showVideoPreview && (
        <ShadowContainer setShowContainer={setShowVideoPreview}>
          <VideoPlayer
            source={playerOptions.source}
            type={playerOptions.type}
          />
        </ShadowContainer>
      )}
      <Modal
        opened={openedModal}
        onClose={() => setOpenedModal(false)}
        title={translate('video:message.modalTitle')}>
        <Dropzone
          accept={[MIME_TYPES.mp4]}
          onChange={(files) => onChange(files[0])}
          placeholder={translate('video:message.dropzonePlaceholder')}
        />
      </Modal>
      <Label>{label}</Label>
      {file ? (
        <FileAddContainer>
          <Thumbnail ref={videoRef} onClick={() => setShowVideoPreview(true)}>
            <source src={playerOptions.source} />
          </Thumbnail>
          <Edit onClick={handleFileClick}>
            <AiTwotoneEdit />
          </Edit>
        </FileAddContainer>
      ) : (
        <Input {...{onClick: handleFileClick, disabled}}>Adicionar video</Input>
      )}
    </Container>
  );
};

export default VideInput;
